import { Link, useLocation } from "react-router-dom";
import { 
  House, Calendar, TrendingUp, Dumbbell, UtensilsCrossed,
  LogOut, Menu, X, Lock, Users, MessageCircle, ClipboardList, LayoutTemplate
} from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { getMembershipConfig, isPaidTier, normaliseTier } from "@/lib/membershipUtils";
import WorkoutTimerPill from "@/components/dashboard/WorkoutTimerPill";
import { BRAND_LOGO } from "@/lib/brandAssets";

const baseNavItems = [
  { path: "/", label: "Hub", icon: House },
  { path: "/sessions", label: "Sessions", icon: Calendar, requiresSessionTier: true },
  { path: "/progress", label: "Progress", icon: TrendingUp },
  { path: "/workouts", label: "Workouts", icon: Dumbbell },
  { path: "/meals", label: "Meal Plans", icon: UtensilsCrossed },
  { path: "/chat", label: "Chat", icon: MessageCircle },
];

// Tiers that can actually book sessions on the Sessions page. Foundation +
// Momentum see the nav item but it renders disabled with a lock icon.
const SESSION_TIERS = new Set(["strength", "elite_coaching"]);

export default function Sidebar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const cfg = getMembershipConfig(user?.membership_type);
  const isPaid = isPaidTier(user?.membership_type);

  const { data: trainers = [] } = useQuery({
    queryKey: ["trainers"],
    queryFn: () => base44.entities.Trainer.list(),
  });

  const isTrainer =
    user?.role === "admin" ||
    user?.role === "trainer" ||
    trainers.some(t => t.user_id === user?.id);

  const handleLogout = () => {
    base44.auth.logout();
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 pb-8 flex justify-center">
        <Link to="/">
          <img
            src={BRAND_LOGO}
            alt="TAFitness"
            className="h-16 object-contain"
          />
        </Link>
      </div>

      <nav className="flex-1 px-3 space-y-1">
        {(() => {
          const tier = normaliseTier(user?.membership_type);
          const canBookSessions =
            SESSION_TIERS.has(tier) || user?.role === "admin" || user?.role === "trainer";
          // Always include the Sessions item — when the user can't book
          // sessions we still render it but in a disabled/locked state so
          // it's discoverable as an upsell.
          const nav = baseNavItems.map((n) =>
            n.requiresSessionTier && !canBookSessions ? { ...n, locked: true } : n,
          );
          return [
            ...nav,
            ...(isTrainer ? [
              { path: "/trainer-clients", label: "My Clients", icon: ClipboardList, roleOnly: true },
              { path: "/workout-templates", label: "Templates", icon: LayoutTemplate, roleOnly: true },
            ] : []),
            ...(user?.role === "admin" ? [{ path: "/admin", label: "Admin", icon: Users, roleOnly: true }] : []),
          ];
        })().map((item) => {
          const isActive = location.pathname === item.path;
          // `item.locked` flag is set per-item above (e.g. Sessions for
          // Foundation/Momentum). Otherwise, fall back to tier-based locking
          // for non-Hub, non-role-only nav items.
          const locked = item.locked || (item.path !== "/" && !item.roleOnly && !isPaid);
          if (locked) {
            return (
              <span
                key={item.path}
                aria-disabled="true"
                title="Upgrade your membership to unlock"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-locked`}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium cursor-not-allowed text-sidebar-foreground/30 hover:bg-sidebar-accent/30"
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                <Lock className="w-3.5 h-3.5 opacity-60" />
              </span>
            );
          }
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                  : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 mt-auto space-y-2">
        {/* Floating workout timer — only renders while a workout is active. */}
        <div className="flex justify-center">
          <WorkoutTimerPill />
        </div>
        {user && (
          <div className="px-4 py-3 rounded-xl bg-sidebar-accent/60">
            <p className="text-xs text-sidebar-foreground/60 truncate">{user.full_name || user.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.sidebarClassName}`}>
                {cfg.label}
              </span>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-all w-full"
        >
          <LogOut className="w-5 h-5" />
          Log Out
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop-only toggle (hidden on tablet/mobile since BottomNav handles those) */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 hidden bg-card shadow-lg"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </Button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-screen w-64 bg-sidebar z-40 transition-transform duration-300 lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <NavContent />
      </aside>
    </>
  );
}